import { useSpecialties } from "@/hooks/useSpecialties";
import { createPublicAPI } from "@/services/apiService";
import { WaitlistService } from "@/services/waitlistService";
import type { WaitlistPrimaryPractice } from "@/types/waitlist";
import type { WaitlistFormFieldErrors, WaitlistViewProps } from "@/types/waitlist";
import {
  getAxiosNetworkFailureErrorMessage,
  getAxiosRateLimitErrorMessage,
  getFastApiValidationDetailMessage,
  getRequestValidationErrorItems,
} from "@/utils/errorUtils";
import {
  collectSurveyErrors,
  getWaitlistAllStepsErrors,
  getWaitlistStep1Errors,
  initialWaitlistSurvey,
} from "@/utils/waitlistValidation";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

import { AuthLayout } from "../AuthLayout";
import { WaitlistSuccessView } from "./WaitlistSuccessView";
import { WaitlistView } from "./WaitlistView";

/** API `field` names from validation errors → `WaitlistFormFieldErrors` keys. */
const API_FIELD_TO_FORM: Partial<Record<string, keyof WaitlistFormFieldErrors>> = {
  name: "name",
  organization_name: "organizationName",
  email: "email",
  contact_phone: "contactPhone",
  specialty_id: "specialty",
};

export const Waitlist = () => {
  const [searchParams] = useSearchParams();
  const rawUtm = searchParams.get("utm_source");
  const utmSource = rawUtm?.trim() ? rawUtm.trim().slice(0, 255) : undefined;

  const [survey, setSurvey] = useState(initialWaitlistSurvey);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [isProfessional, setIsProfessional] = useState(false);
  const [specialtyId, setSpecialtyId] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<WaitlistFormFieldErrors>({});
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);

  const { specialties, isError: specialtiesError } = useSpecialties();
  const publicApi = useMemo(() => createPublicAPI("v2"), []);
  const waitlistService = useMemo(() => new WaitlistService(publicApi), [publicApi]);

  useEffect(() => {
    if (specialtiesError) {
      toast.error("Failed to load specialties. Please contact support if problem persists.");
    }
  }, [specialtiesError]);

  const validateStep1 = (): boolean => {
    const e = getWaitlistStep1Errors({ email, name, organizationName, contactPhone });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = (): boolean => {
    const e = collectSurveyErrors(survey);
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /** Validates contact, survey, and professional fields before POST; focuses the first step with errors. */
  const validateAllStepsForSubmit = (): boolean => {
    const e = getWaitlistAllStepsErrors({
      email,
      name,
      organizationName,
      contactPhone,
      survey,
      isProfessional,
      specialtyId,
    });
    setErrors(e);
    if (Object.keys(e).length === 0) return true;
    if (e.email || e.name || e.organizationName || e.contactPhone) {
      setStep(1);
    } else if (e.primaryPracticeArea || e.reportsPerMonth || e.pages || e.caseFlow) {
      setStep(2);
    } else {
      setStep(3);
    }
    return false;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleBack = () => {
    if (step > 1) {
      setErrors({});
      setStep((s) => s - 1);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validateAllStepsForSubmit()) return;

    setIsSubmitting(true);
    try {
      await waitlistService.submit({
        email: email.trim(),
        name: name.trim(),
        organization_name: organizationName.trim(),
        is_professional: isProfessional,
        contact_phone: contactPhone,
        primary_practice_area: survey.primaryPracticeArea as WaitlistPrimaryPractice,
        reports_per_month: parseInt(survey.reportsPerMonth, 10),
        pages_min: parseInt(survey.pagesMin, 10),
        pages_max: parseInt(survey.pagesMax, 10),
        case_flow_consistent: survey.caseFlowConsistent as boolean,
        ...(isProfessional ? { specialty_id: specialtyId } : {}),
        ...(utmSource ? { utm_source: utmSource } : {}),
      });
      setSuccess(true);
    } catch (error) {
      const rateOrNetwork =
        getAxiosRateLimitErrorMessage(error) ?? getAxiosNetworkFailureErrorMessage(error);
      if (rateOrNetwork) {
        setErrors((prev) => ({ ...prev, general: rateOrNetwork }));
      } else {
        const items = getRequestValidationErrorItems(error);
        const fromApi: Partial<WaitlistFormFieldErrors> = {};
        const unmapped: string[] = [];
        for (const { field, message } of items) {
          const key = API_FIELD_TO_FORM[field];
          if (key) fromApi[key] = message;
          else unmapped.push(message);
        }
        const general =
          unmapped.length > 0
            ? unmapped.join(" ")
            : Object.keys(fromApi).length === 0
              ? getFastApiValidationDetailMessage(error)
              : undefined;
        setErrors((prev) => ({ ...prev, ...fromApi, general }));
        if (fromApi.name || fromApi.organizationName || fromApi.email || fromApi.contactPhone) {
          setStep(1);
        } else if (fromApi.specialty) {
          setStep(3);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewProps: WaitlistViewProps = {
    step,
    survey,
    email,
    name,
    organizationName,
    isProfessional,
    specialtyId,
    contactPhone,
    specialties,
    isSubmitting,
    errors,
    onSurveyChange: (patch) => {
      setSurvey((s) => ({ ...s, ...patch }));
      setErrors((prev) => {
        const next = { ...prev };
        if ("primaryPracticeArea" in patch) delete next.primaryPracticeArea;
        if ("reportsPerMonth" in patch) delete next.reportsPerMonth;
        if ("pagesMin" in patch || "pagesMax" in patch) delete next.pages;
        if ("caseFlowConsistent" in patch) delete next.caseFlow;
        return next;
      });
    },
    onEmailChange: (val) => {
      setEmail(val);
      setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
    },
    onNameChange: (val) => {
      setName(val);
      setErrors((prev) => (prev.name ? { ...prev, name: undefined } : prev));
    },
    onOrganizationNameChange: (val) => {
      setOrganizationName(val);
      setErrors((prev) =>
        prev.organizationName ? { ...prev, organizationName: undefined } : prev
      );
    },
    onIsProfessionalChange: (val) => {
      setIsProfessional(val);
      setErrors((prev) => (prev.specialty ? { ...prev, specialty: undefined } : prev));
    },
    onSpecialtyIdChange: (val) => {
      setSpecialtyId(val);
      setErrors((prev) => (prev.specialty ? { ...prev, specialty: undefined } : prev));
    },
    onContactPhoneChange: (val) => {
      setContactPhone(val);
      setErrors((prev) => (prev.contactPhone ? { ...prev, contactPhone: undefined } : prev));
    },
    onNext: handleNext,
    onBack: handleBack,
    onSubmit: handleSubmit,
  };

  if (success) {
    return (
      <AuthLayout>
        <WaitlistSuccessView />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <WaitlistView {...viewProps} />
    </AuthLayout>
  );
};
